import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function LeadershipFacilitation() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Strategic Planning and Organizational Direction',
      description: 'Facilitating leadership discussions that clarify long-term strategy, identify organizational priorities, and align leadership around shared goals.'
    },
    {
      title: 'Leadership Team Alignment',
      description: 'Helping senior leadership teams strengthen communication, clarify roles, build trust, and establish effective decision-making practices.'
    },
    {
      title: 'Organizational Change and Transformation',
      description: 'Supporting leadership teams as they navigate restructuring, leadership transitions, or shifts in strategy and organizational priorities.'
    },
    {
      title: 'Executive Retreats and Strategy Sessions',
      description: 'Designing and facilitating executive retreats that allow leadership teams to step back from day-to-day work and focus on strategic thinking and organizational direction.'
    },
    {
      title: 'Organizational and Leadership Assessments',
      description: 'Conducting leadership and organizational diagnostics to identify strengths, gaps, and opportunities. These assessments may include leadership team effectiveness reviews, organizational health assessments, and structured leadership discussions.'
    },
    {
      title: '360-Degree Leadership Reviews',
      description: 'Designing and facilitating comprehensive leadership feedback processes for executives and senior leaders. These structured reviews gather insights from colleagues, direct reports, and key stakeholders to support leadership development and organizational effectiveness.'
    }
  ];

  const engagements = [
    'Executive leadership retreats',
    'Strategic planning and priority-setting processes',
    'Senior leadership team alignment work',
    'Organizational and leadership assessments',
    '360-degree leadership feedback processes',
    'Multi-session leadership development engagements'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1a2b4a] to-[#0f1a2e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 border-4 border-white rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 border-4 border-white rounded-full -ml-32 -mb-32"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="inline-block mb-6">
            <span className="text-[#2d9b9b] text-sm font-semibold tracking-wide uppercase">Core Service</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Crimson Text, serif' }}>
            Leadership Team Facilitation
          </h1>
          <p className="text-xl text-white/90 leading-relaxed max-w-3xl">
            Strong leadership teams are essential for organizations navigating complexity, growth, and change. I partner with nonprofit executives and senior leadership teams to guide strategic conversations, strengthen leadership alignment, and help organizations move forward with clarity and shared direction.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#1a2b4a]/80 leading-relaxed mb-6">
              My facilitation work supports leadership teams during critical organizational moments—strategic planning, leadership transitions, structural change, and periods of growth or uncertainty. Through structured dialogue, leadership diagnostics, and thoughtful facilitation, I help teams move beyond day-to-day operational pressures to focus on the strategic decisions that shape the organization's future.
            </p>
          </div>
        </div>
      </section>

      {/* What Leadership Team Facilitation Can Support */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
              What Leadership Team Facilitation Can Support
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-[#2d9b9b]"
              >
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-4">
                  {service.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Approach
          </h2>
          
          <div className="space-y-6 text-lg text-[#1a2b4a]/80 leading-relaxed">
            <p>
              I bring both executive leadership experience and organizational development expertise to my facilitation work. Having served as Chief Operating Officer, Chief People & Culture Officer, and Chief of Staff at Greenpeace USA, I understand the real-world dynamics and pressures leadership teams face.
            </p>
            <p>
              My approach combines strategic advisory, organizational diagnostics, and skilled facilitation to help leadership teams surface critical issues, navigate complex decisions, and build stronger alignment across the organization.
            </p>
            <p>
              Each engagement is tailored to the organization's needs and typically includes advance consultation with leadership, leadership and organizational assessments, custom session design, facilitated leadership sessions or retreats, and follow-up guidance to support implementation.
            </p>
          </div>
        </div>
      </section>

      {/* Typical Engagements */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Typical Engagements
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {engagements.map((engagement, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="w-2 h-2 bg-[#2d9b9b] rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-base text-[#1a2b4a]">{engagement}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-white rounded-2xl shadow-md border-l-4 border-[#2d9b9b]">
            <p className="text-lg text-[#1a2b4a]/80 leading-relaxed">
              Organizations typically engage me when leadership teams need an experienced facilitator and strategic advisor to guide important organizational conversations and help translate strategy into aligned leadership and action.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2d9b9b]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            Let's Connect
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10">
            If your leadership team is navigating an important strategic moment and would benefit from experienced facilitation and guidance, reach out to start a conversation.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="mailto:jerilyn@jerilynemerson.com"
              className="inline-flex items-center gap-2 bg-white text-[#2d9b9b] px-8 py-4 rounded-full hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer font-semibold"
            >
              <i className="ri-mail-line"></i>
              jerilyn@jerilynemerson.com
            </a>
            <a 
              href="https://linkedin.com/in/jerilynemerson"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white hover:text-[#2d9b9b] transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold"
            >
              <i className="ri-linkedin-line"></i>
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}